import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import axios from "axios";
import { 
  User, Calendar, Clock, Settings, Copy, ExternalLink,
  CheckCircle, AlertCircle, Menu, X, Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRODUCTION_URL = "https://apoyum.com";

const LEGAL_DISCLAIMER = "Este servicio es de acompañamiento y escucha. No es un servicio médico y no sustituye atención profesional.";

const ListenerSidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  
  const links = [
    { href: "/listener", label: "Resumen", icon: User },
    { href: "/listener/perfil", label: "Mi perfil", icon: Settings },
    { href: "/listener/disponibilidad", label: "Disponibilidad", icon: Calendar },
    { href: "/listener/sesiones", label: "Sesiones", icon: Clock },
    { href: "/listener/usuarios", label: "Mis Usuarios", icon: Users },
    { href: "/listener/configuracion", label: "Configuración", icon: Settings },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white lg:bg-transparent
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        lg:shrink-0 lg:block
        border-r lg:border-r-0 border-stone-200
        pt-4 lg:pt-0
      `}>
        {/* Mobile close button */}
        <div className="flex justify-end p-4 lg:hidden">
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <nav className="space-y-1 px-4 lg:px-0">
          {links.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={onClose}
              className={`sidebar-link ${location.pathname === link.href ? 'active' : ''}`}
            >
              <link.icon className="w-5 h-5" />
              {link.label}
            </Link>
          ))}
        </nav>
      </aside>
    </>
  );
};

export const ListenerLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  return (
    <Layout showFooter={false}>
      <div className="container-main py-4 lg:py-8">
        {/* Mobile header with menu button */}
        <div className="flex items-center gap-4 mb-4 lg:hidden">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-stone-100 rounded-lg"
            aria-label="Abrir menú"
          >
            <Menu className="w-6 h-6" />
          </button>
          {title && (
            <h1 className="text-xl font-serif font-bold text-foreground">{title}</h1>
          )}
        </div>
        
        <div className="flex gap-8">
          <ListenerSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          <main className="flex-1 min-w-0 w-full">
            {title && (
              <h1 className="hidden lg:block text-2xl font-serif font-bold text-foreground mb-6">{title}</h1>
            )}
            {children}
          </main>
        </div>
      </div>
    </Layout>
  );
};

export default function ListenerDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/listener/profile`),
        axios.get(`${API}/listener/sessions`)
      ]);
      setProfile(profileRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyProfileLink = () => {
    if (profile?.username) {
      const link = `${PRODUCTION_URL}/l/${profile.username}`;
      navigator.clipboard.writeText(link);
      toast.success("Enlace copiado al portapapeles");
    }
  };

  const upcomingSessions = sessions.filter(s => s.status === 'confirmed').slice(0, 5);

  if (loading) {
    return (
      <ListenerLayout title="Mi panel">
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      </ListenerLayout>
    );
  }

  return (
    <ListenerLayout>
      <div className="space-y-8">
        {/* Welcome */}
        <div>
          <h1 className="text-2xl font-serif font-bold text-foreground">Hola, {user?.name}</h1>
          <p className="mt-1 text-stone-600">Bienvenido/a a tu panel de listener</p>
        </div>

        {/* Profile Status */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                profile?.status === 'approved' ? 'bg-green-100' : 
                profile?.status === 'pending' ? 'bg-amber-100' : 'bg-red-100'
              }`}>
                {profile?.status === 'approved' ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <AlertCircle className={`w-6 h-6 ${profile?.status === 'pending' ? 'text-amber-600' : 'text-red-600'}`} />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Estado del perfil</h3>
                <p className={`text-sm ${
                  profile?.status === 'approved' ? 'text-green-600' : 
                  profile?.status === 'pending' ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {profile?.status === 'approved' ? 'Aprobado - Tu perfil está visible' : 
                   profile?.status === 'pending' ? 'Pendiente de aprobación' : 'Rechazado'}
                </p>
              </div>
            </div>
            <Link to="/listener/perfil">
              <Button variant="outline" data-testid="edit-profile-btn">Editar perfil</Button>
            </Link>
          </div>
        </div>

        {/* Profile Link */}
        {profile?.status === 'approved' && profile?.username && (
          <div className="bg-primary/5 rounded-2xl border border-primary/20 p-6">
            <h3 className="font-semibold text-foreground mb-2">Tu enlace personal</h3>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white px-4 py-2 rounded-lg text-sm text-foreground border border-stone-200">
                {PRODUCTION_URL}/l/{profile.username}
              </code>
              <Button variant="outline" size="icon" onClick={copyProfileLink} data-testid="copy-link-btn">
                <Copy className="w-4 h-4" />
              </Button>
              <a href={`/l/${profile.username}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="icon" data-testid="view-profile-link">
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <p className="text-sm text-stone-500">Sesiones totales</p>
            <p className="text-3xl font-bold text-foreground mt-1">{sessions.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <p className="text-sm text-stone-500">Precio por sesión</p>
            <p className="text-3xl font-bold text-primary mt-1">${profile?.price_per_session}</p>
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <p className="text-sm text-stone-500">Tu porcentaje</p>
            <p className="text-3xl font-bold text-foreground mt-1">{profile?.revenue_percentage}%</p>
          </div>
        </div>

        {/* Upcoming Sessions */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Próximas sesiones</h3>
            <Link to="/listener/sesiones" className="text-primary text-sm font-medium hover:underline">
              Ver todas
            </Link>
          </div>
          {upcomingSessions.length === 0 ? (
            <p className="text-stone-500 text-center py-8">No tienes sesiones programadas</p>
          ) : (
            <div className="space-y-3">
              {upcomingSessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between py-3 border-b border-stone-100 last:border-0">
                  <div>
                    <p className="font-medium text-foreground">{session.user_name}</p>
                    <p className="text-sm text-stone-500">{session.date} - {session.time}</p>
                  </div>
                  <span className="badge-approved">Confirmada</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="legal-disclaimer text-center">
          {LEGAL_DISCLAIMER}
        </p>
      </div>
    </ListenerLayout>
  );
}
